import React, { useEffect, useState } from "react";
import SEO from "../components/SEO";
import GallerySection from "../components/GallerySection";
import CleanupSection from "../components/CleanupSection";
import ContactForm from "../components/ContactForm";
import { getCMSCollection, getOptimizedImageUrl, getGalleryByRef } from "../lib/cms";

export default function Beskaering() {
  const [galleries, setGalleries] = useState([]);

  useEffect(() => {
    async function loadData() {
      const g = await getCMSCollection("galleries");
      setGalleries(g);
    }
    loadData();
  }, []);

  const gallery = getGalleryByRef(galleries, "gallery-beskaering") || getGalleryByRef(galleries, "beskaering");
  const galleryImages = gallery?.images || [];

  return (
    <>
      <SEO
        title="Beskæring"
        description="Professionel og sikker træfældning af alle typer træer – fra simple opgaver til risikofyldte fældninger tæt på bygninger. Erfaring og uddannelse sikrer en veludført opgave."
        ogImage="https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/472335433-602814825870936-6000153368973253049-n_1db6f762.jpeg"
      />

      {/* Hero Header */}
      <section className="relative bg-[#12281D] text-white py-16 sm:py-24 border-b border-[#1E4231] overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedImageUrl(
              "https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/472335433-602814825870936-6000153368973253049-n.jpeg",
              1600,
              80
            )}
            alt="Beskæring af træer"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#12281D] via-[#12281D]/80 to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight">
              Beskæring
            </h1>
            <p className="mt-4 text-lg sm:text-xl text-[#C0DACB] leading-relaxed">
              Beskæring der skaber lys, luft og balance – samtidig med at træets sundhed, form og levetid bevares.
            </p>

            <div className="mt-8 flex flex-wrap gap-4 items-center">
              <a
                href="tel:42169230"
                className="inline-flex items-center gap-2.5 bg-[#D9691E] hover:bg-[#C25B15] text-white px-6 py-3.5 rounded-xl font-bold text-sm sm:text-base shadow-lg transition-all whitespace-nowrap"
              >
                <i className="fa-solid fa-phone" aria-hidden="true" />
                <span className="whitespace-nowrap">Ring 42 16 92 30</span>
              </a>
              <a
                href="#kontakt-formular"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3.5 rounded-xl font-semibold text-sm sm:text-base border border-white/20 transition-all whitespace-nowrap"
              >
                <span>Få uforpligtende tilbud</span>
                <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Section (Verbatim scrape prose) */}
      <section className="py-16 sm:py-24 bg-[#F2EDE2]/70 border-y border-[#E2D8C8] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-y-8 lg:gap-y-0 relative">
            {/* Image Side: Bredere og højere, placeret bag ved tekstboksen med overlap */}
            <div className="lg:col-span-6 lg:row-start-1 lg:col-start-1 relative z-0">
              <div className="relative rounded-2xl overflow-hidden h-[420px] sm:h-[500px] lg:h-[620px] w-full">
                <img
                  src={getOptimizedImageUrl(
                    "https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/490121453-664083986418506-779564397277214703-n.jpeg",
                    1200,
                    85
                  )}
                  alt="Træbeskæring for mere lys i haven"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Content Side: Boks med baggrundsfarve #1e5d31, ingen shadows eller border, overlapper billedet foran */}
            <div className="lg:col-span-7 lg:row-start-1 lg:col-start-6 relative z-10 bg-[#1e5d31] rounded-2xl p-6 sm:p-9 lg:p-12 space-y-6 shadow-none border-0">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight">
                Beskæring der skaber lys, luft og balance
              </h2>

              {/* Exact scraped prose */}
              <div className="space-y-4 text-base sm:text-lg text-[#D5E5DB] leading-relaxed font-normal">
                <p>
                  Er træet blevet for stort, kan en beskæring være en effektiv løsning til at skabe mere lys og luft i haven.
                </p>
                <p>
                  En korrekt udført beskæring forbedrer både træets sundhed og dets udseende, samtidig med at det tilpasses omgivelserne.
                </p>

                {/* Highlight box for Kroneløft og Kronereducering */}
                <div className="space-y-4 my-6">
                  <div className="p-5 rounded-xl bg-white/10 border border-white/15">
                    <h3 className="font-bold text-lg text-white flex items-center gap-2">
                      <i className="fa-solid fa-arrow-up-from-bracket text-[#F39C5A]" aria-hidden="true" />
                      Kroneløft
                    </h3>
                    <p className="text-sm text-[#D5E5DB] mt-1.5 leading-relaxed">
                      Ved et <strong className="text-white">kroneløft</strong> fjernes de nederste grene, så der skabes mere frihøjde under træet. Det giver bedre plads, mere lys til plæne og bede samt et lettere og mere harmonisk udtryk.
                    </p>
                  </div>

                  <div className="p-5 rounded-xl bg-white/10 border border-white/15">
                    <h3 className="font-bold text-lg text-white flex items-center gap-2">
                      <i className="fa-solid fa-compress text-[#F39C5A]" aria-hidden="true" />
                      Kronereducering
                    </h3>
                    <p className="text-sm text-[#D5E5DB] mt-1.5 leading-relaxed">
                      Ved en <strong className="text-white">kronereducering</strong> mindskes hele kronens omfang, så træet bliver mindre dominerende. Denne metode er velegnet, når træet skygger for meget, står tæt på bygninger eller påvirker udsigten.
                    </p>
                  </div>
                </div>

                <p className="font-semibold text-white">
                  Skånsom beskæring udført med faglig viden sikrer, at træet bevarer sin styrke, form og levetid.
                </p>
                <p>
                  Regelmæssig pleje forebygger desuden stormskader og nedfaldne grene.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTEGRERET OPRYDNING */}
      <CleanupSection />

      {/* CONNECTED CMS GALLERY (TECHNICAL REFERENCE via slug: gallery-beskaering) */}
      <GallerySection
        title="Galleri: Beskæring"
        subtitle="Se eksempler på udført beskæring og kroneløft i haven"
        images={galleryImages}
        className="bg-[#F4EFE6]/60 border-t border-[#E5DDCF]"
      />

      {/* CONTACT SECTION WITH FORM */}
      <section className="py-16 sm:py-24 bg-[#14291E] text-white relative overflow-hidden" id="kontakt-formular">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-y-8 lg:gap-y-0 relative">
            <div className="lg:col-span-6 lg:row-start-1 lg:col-start-1 relative z-0 space-y-6 lg:pr-8">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight lg:max-w-[75%]">
                Trænger dine træer til mere lys og luft?
              </h2>
              <p className="text-base text-[#BFD9CA] leading-relaxed max-w-lg lg:max-w-[75%]">
                Kontakt mig for en vurdering af dine træer. Jeg rådgiver altid ærligt om den rette beskæringsform og tidspunkt for dine træarter.
              </p>

              {/* Direct Info Cards - unified single box overlapping slightly under form to the right */}
              <div className="bg-[#1C3B2C] p-6 sm:p-7 rounded-2xl border border-[#2B543F] space-y-4 relative z-0 lg:-mr-8 lg:pr-14 shadow-none">
                <div className="flex items-center gap-4 pb-4 border-b border-white/10 text-white">
                  <div className="w-8 flex items-center justify-center shrink-0 text-xl text-[#D9691E]">
                    <i className="fa-solid fa-phone" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs text-[#8DB59E] block uppercase tracking-wider font-semibold">
                      Ring direkte
                    </span>
                    <a
                      href="tel:42169230"
                      className="text-lg font-extrabold text-white hover:text-[#F39C5A] transition-colors whitespace-nowrap"
                    >
                      42 16 92 30
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4 pb-4 border-b border-white/10 text-white">
                  <div className="w-8 flex items-center justify-center shrink-0 text-xl text-[#D9691E]">
                    <i className="fa-regular fa-envelope text-[#D9691E] text-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs text-[#8DB59E] block uppercase tracking-wider font-semibold">
                      Send en email
                    </span>
                    <a
                      href="mailto:info@casperstraeservice.dk"
                      className="text-base font-bold text-white hover:text-[#F39C5A] transition-colors whitespace-nowrap"
                    >
                      info@casperstraeservice.dk
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-white">
                  <div className="w-8 flex items-center justify-center shrink-0 text-xl text-[#D9691E]">
                    <i className="fa-solid fa-location-dot text-[#D9691E] text-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-xs text-[#8DB59E] block uppercase tracking-wider font-semibold">
                      Adresse
                    </span>
                    <span className="text-sm font-medium text-white block">
                      Caspers Træservice • Folevej 23, 6510 Gram
                    </span>
                    <span className="text-xs text-[#8DB59E]">CVR: 37232521</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 lg:row-start-1 lg:col-start-6 relative z-10">
              <ContactForm defaultService="Beskæring" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
